import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { trapTab } from '../ui/focus';
import { SplitDialog } from './SplitDialog';
import { ModelHint } from './ModelHint';
import { PAYMENTS_KEY, RECEIVABLES_RESOURCE, SIGNALS, type DismissedItem, type ReviewItem, type ReviewSuggestion } from './types';
import { DialogLayer } from '../ui/Dialog';

export const REVIEW_KEY = [...PAYMENTS_KEY, 'review'] as const;

/** "Nie dotyczy": why the operation is not a payment of a receivable (own transfer, refund, fee…). */
function DismissDialog({ item, onClose, onSaved }: { item: ReviewItem; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('');
  const save = useMutation({ mutationFn: () => api(`/api/payments/transactions/${item.transaction.id}/dismiss`, { method: 'POST', body: { reason: reason.trim() } }), onSuccess: onSaved });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dismiss-title"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="dismiss-title">Nie dotyczy</h2>
        <p className="muted">
          {item.unallocatedGrosze !== undefined && item.transaction.amountGrosze !== undefined && item.unallocatedGrosze < Math.abs(item.transaction.amountGrosze)
            ? 'Pozostała kwota zniknie z „Do zatwierdzenia”. Przydzielone części zostają zaksięgowane.'
            : 'Operacja zniknie z „Do zatwierdzenia”. Nic nie zostanie zaksięgowane.'}
        </p>
        <label className="field">
          <span>Powód (np. przelew własny, zwrot, opłata)</span>
          <input autoFocus value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </label>
        {save.error && (
          <p className="alert" role="alert">
            {save.error instanceof ApiError ? save.error.message : 'Nie udało się zapisać'}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            Nie dotyczy
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

/** What a suggestion points at; money out on a receivable is a refund of what the client overpaid (F40 "Do zwrotu"). */
function target(s: ReviewSuggestion): { label: string; who: string; remaining?: number; refund?: boolean } {
  if (s.receivable) {
    const out = s.refund;
    const left = out ? s.receivable.refundGrosze : s.receivable.remainingGrosze;
    return { label: out ? `Zwrot: ${s.receivable.label}` : s.receivable.label, who: [s.receivable.contractorName, s.receivable.eventName].filter(Boolean).join(' · '), ...(left !== undefined ? { remaining: left } : {}), refund: out };
  }
  const p = s.payable!;
  return { label: p.label, who: p.source === 'settlement' ? 'wypłata przelewem' : 'faktura zakupowa', ...(p.remainingGrosze !== undefined ? { remaining: p.remainingGrosze } : {}) };
}

/** "Nie dotyczy" (review I3): the dismissed operations with why, each can go back with "Przywróć". */
function DismissedList() {
  const queryClient = useQueryClient();
  const dismissed = useQuery({ queryKey: [...REVIEW_KEY, 'dismissed'], queryFn: () => api<{ items: DismissedItem[] }>('/api/payments/review?dismissed=1') });
  const restore = useMutation({
    mutationFn: (id: string) => api(`/api/payments/transactions/${id}/restore`, { method: 'POST', body: {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
  const items = dismissed.data?.items ?? [];
  return (
    <>
      {(dismissed.error || restore.error) && (
        <p className="alert" role="alert">
          {(dismissed.error ?? restore.error) instanceof ApiError ? ((dismissed.error ?? restore.error) as ApiError).message : 'Nie udało się wczytać operacji'}
        </p>
      )}
      {dismissed.data && items.length === 0 && <p className="muted">Żadna operacja nie jest oznaczona „Nie dotyczy”.</p>}
      <ul className="pay-review" aria-label="Operacje „Nie dotyczy”">
        {items.map(({ transaction: t, dismissReason }) => (
          <li key={t.id} className="pay-review-item" aria-label={`Operacja ${t.title || t.counterpartyName}`}>
            <div className="pay-review-head">
              <div>
                <strong>{t.counterpartyName || 'bez nazwy'}</strong>
                <span className="pay-sub">
                  {formatDayText(t.bookingDate)} · {t.title || 'bez tytułu'}
                </span>
                {dismissReason && <span className="pay-sub">Powód: {dismissReason}</span>}
              </div>
              {t.amountGrosze !== undefined && <span className="pay-review-amount">{formatPln(t.amountGrosze)}</span>}
            </div>
            <div className="pay-review-actions">
              <button className="btn ghost small" type="button" disabled={restore.isPending} onClick={() => restore.mutate(t.id)}>
                Przywróć
              </button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * "Finanse → Płatności → Do zatwierdzenia" (F38): bank operations the program did not book itself (or only partly —
 * with what is left), each with its suggestions (a possible duplicate of a manual transfer first, then by points);
 * "Zatwierdź" books one with a click — with the amounts shown, else the server answers 409 and the list is refreshed —
 * "Połącz" links a manual transfer, "Podziel / inna…" splits by hand, "Nie dotyczy" takes the operation off the list
 * (and "Nie dotyczy" shows them, to restore).
 */
export function ReviewPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [showDismissed, setShowDismissed] = useState(false);
  const review = useQuery({ queryKey: REVIEW_KEY, queryFn: () => api<{ items: ReviewItem[] }>('/api/payments/review') });
  const [splitting, setSplitting] = useState<ReviewItem | null>(null);
  const [dismissing, setDismissing] = useState<ReviewItem | null>(null);
  const approve = useMutation({
    mutationFn: (v: { transactionId: string; suggestion: ReviewSuggestion }) => api(`/api/payments/transactions/${v.transactionId}/approve`, { method: 'POST', body: { suggestionId: v.suggestion.id, check: v.suggestion.check } }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
  });
  if (!me) return null;
  const done = () => {
    setSplitting(null);
    setDismissing(null);
    void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
  };
  const items = review.data?.items ?? [];
  const mayType = fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  const approveError =
    approve.error instanceof ApiError && approve.error.status === 409
      ? 'Kwoty zmieniły się w międzyczasie (ktoś odnotował wpłatę albo przydzielił operację). Lista jest odświeżona — sprawdź propozycję i zatwierdź jeszcze raz.'
      : approve.error instanceof ApiError
        ? approve.error.message
        : approve.error
          ? 'Nie udało się zatwierdzić'
          : '';
  return (
    <div data-feature-id="payments.review">
      <div className="segment pay-review-filter" role="group" aria-label="Które operacje">
        <button type="button" aria-pressed={!showDismissed} onClick={() => setShowDismissed(false)}>
          Czekające
        </button>
        <button type="button" aria-pressed={showDismissed} onClick={() => setShowDismissed(true)}>
          Nie dotyczy
        </button>
      </div>
      {showDismissed ? (
        <DismissedList />
      ) : (
        <>
          {review.error && (
            <p className="alert" role="alert">
              {review.error instanceof ApiError ? review.error.message : 'Nie udało się wczytać operacji'}
            </p>
          )}
          {approveError && (
            <p className="alert" role="alert">
              {approveError}
            </p>
          )}
          {review.data && items.length === 0 && <p className="muted">Nic do zatwierdzenia. Operacje z wczytanych wyciągów są przydzielone albo oznaczone „Nie dotyczy”.</p>}
          <ul className="pay-review" aria-label="Operacje do zatwierdzenia">
            {items.map((item) => {
              const t = item.transaction;
              // what is left of the operation (all of it unless partly allocated)
              const rest = item.unallocatedGrosze;
              const partly = rest !== undefined && t.amountGrosze !== undefined && rest < Math.abs(t.amountGrosze);
              return (
                <li key={t.id} className="pay-review-item" aria-label={`Operacja ${t.title || t.counterpartyName}`}>
                  <div className="pay-review-head">
                    <div>
                      <strong>{t.counterpartyName || 'bez nazwy'}</strong>
                      <span className="pay-sub">
                        {formatDayText(t.bookingDate)} · {t.title || 'bez tytułu'}
                      </span>
                    </div>
                    {t.amountGrosze !== undefined && (
                      <span className="pay-review-amount" data-out={t.amountGrosze < 0 || undefined}>
                        {formatPln(t.amountGrosze)}
                        {partly && <span className="pay-sub">Do przydzielenia: {formatPln(rest)}</span>}
                      </span>
                    )}
                  </div>
                  {item.suggestions.length === 0 ? (
                    <p className="muted small">Brak propozycji — wybierz należność ręcznie albo oznacz „Nie dotyczy”.</p>
                  ) : (
                    <ul className="pay-suggestions" aria-label="Propozycje">
                      {item.suggestions.map((s, i) => {
                        const x = target(s);
                        // money in beyond what is left: approving books it all, the excess is the client's credit (F36)
                        const excess = s.receivable && !s.refund && !s.allocationId && rest !== undefined && x.remaining !== undefined && x.remaining > 0 && rest > x.remaining ? rest - x.remaining : 0;
                        return (
                          <li key={s.id}>
                            <div>
                              {x.label}
                              {x.who && <span className="muted"> · {x.who}</span>}
                              {s.allocationId && s.reason === 'inna wpłata ręczna o tej kwocie' ? (
                                <span className="pay-sub">Inna wpłata ręczna o tej kwocie (przelew wpisany ręcznie, ±7 dni) — nic jej nie łączy z nadawcą; połącz tylko, jeśli to na pewno ta sama wpłata.</span>
                              ) : s.allocationId ? (
                                <span className="pay-sub pay-duplicate">
                                  Możliwy duplikat wpłaty ręcznej (przelew wpisany ręcznie, ta sama kwota, ±7 dni){s.signals.length > 0 ? ` · ${s.signals.map((sig) => SIGNALS[sig] ?? sig).join(', ')}` : ''} — połącz ją z tą operacją zamiast księgować drugi raz.
                                </span>
                              ) : s.source === 'model' ? (
                                <>
                                  <ModelHint reason={s.reason} />
                                  {x.remaining !== undefined && <span className="pay-sub">pozostało {formatPln(x.remaining)}</span>}
                                </>
                              ) : (
                                <span className="pay-sub">
                                  {s.signals.map((sig) => SIGNALS[sig] ?? sig).join(', ')}
                                  {x.remaining !== undefined ? ` · ${x.refund ? 'do zwrotu' : 'pozostało'} ${formatPln(x.remaining)}` : ''}
                                </span>
                              )}
                              {excess > 0 && <span className="pay-sub">Nadwyżka {formatPln(excess)} zostanie jako nadpłata klienta.</span>}
                            </div>
                            <button className={i === 0 ? 'btn primary small' : 'btn small'} type="button" disabled={approve.isPending} onClick={() => approve.mutate({ transactionId: t.id, suggestion: s })}>
                              {s.allocationId ? 'Połącz' : 'Zatwierdź'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="pay-review-actions">
                    {mayType && (
                      <button className="btn ghost small" type="button" onClick={() => setSplitting(item)}>
                        {item.suggestions.length > 0 ? 'Podziel / inna…' : 'Wybierz należność…'}
                      </button>
                    )}
                    <button className="btn ghost small" type="button" onClick={() => setDismissing(item)}>
                      {partly ? 'Nie dotyczy pozostałej kwoty' : 'Nie dotyczy'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {splitting && <SplitDialog item={splitting} onClose={() => setSplitting(null)} onSaved={done} />}
      {dismissing && <DismissDialog item={dismissing} onClose={() => setDismissing(null)} onSaved={done} />}
    </div>
  );
}
